import { useLightswitch } from './useLightswitch.js';

/**
 * Lightswitch Authentication Hook
 * 
 * Custom hook that provides authentication functions for sign in/sign up buttons.
 * This hook provides functions that can be directly attached to button onClick handlers.
 */

/**
 * Hook for Lightswitch authentication actions
 * @returns {Object} Lightswitch authentication functions and state
 */
const useLightswitchAuth = () => {
  const {
    isAuthenticated,
    isLoading,
    user,
    login,
    signup,
    logout,
    isInitialized,
    isReady
  } = useLightswitch();

  /**
   * Handle Lightswitch sign in button click
   * Redirects to Lightswitch login page
   * @param {string} [redirectPath] - Path to redirect back to after login (optional, defaults to current page)
   */
  const handleLightswitchSignIn = (redirectPath) => {
    if (!isInitialized || !isReady) {
      console.warn('Lightswitch SDK not ready yet');
      return;
    }
    
    try {
      // Convert path to full URL if provided
      const fullRedirectUrl = redirectPath 
        ? `${window.location.origin}${redirectPath}`
        : undefined;
      
      login(fullRedirectUrl);
    } catch (error) {
      console.error('Sign in error:', error);
    }
  };

  /**
   * Handle Lightswitch sign up button click
   * Redirects to Lightswitch signup page
   * @param {string} [redirectPath] - Path to redirect back to after signup (optional, defaults to current page)
   */
  const handleLightswitchSignUp = (redirectPath) => {
    if (!isInitialized || !isReady) {
      console.warn('Lightswitch SDK not ready yet');
      return;
    }
    
    try {
      // Convert path to full URL if provided
      const fullRedirectUrl = redirectPath 
        ? `${window.location.origin}${redirectPath}`
        : undefined;
      
      signup(fullRedirectUrl);
    } catch (error) {
      console.error('Sign up error:', error);
    }
  };

  /**
   * Handle Lightswitch logout button click
   * Logs out the current user
   */
  const handleLightswitchLogout = async () => {
    if (!isInitialized || !isReady) {
      console.warn('Lightswitch SDK not ready yet');
      return;
    }
    
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  /**
   * Handle combined Lightswitch auth action (login if not authenticated, logout if authenticated)
   * @param {string} [redirectPath] - Path to redirect back to after login (optional)
   */
  const handleLightswitchAuthToggle = (redirectPath) => {
    if (!isInitialized || !isReady) {
      console.warn('Lightswitch SDK not ready yet');
      return;
    }
    
    if (isAuthenticated) {
      handleLightswitchLogout();
    } else {
      handleLightswitchSignIn(redirectPath);
    }
  };

  return {
    // Authentication state
    isAuthenticated,
    isLoading,
    user,
    isReady: isReady && isInitialized,
    
    // Action handlers for buttons
    handleLightswitchSignIn,
    handleLightswitchSignUp,
    handleLightswitchLogout,
    handleLightswitchAuthToggle,
    
    // Convenience function for checking if actions are available
    canPerformAuth: isReady && isInitialized
  };
};

export { useLightswitchAuth };
